import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllCoursesOptions,
	getSchoolsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import type { Course } from "@/lib/client/types.gen";
import type { StudentAttendanceRow, SchoolDayAttendance, ExcelImportResult, ExcelImportIssue, NewEnrollmentExcelRow } from "@/types/attendance";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Users, Download, Upload, Save, AlertTriangle, CheckCircle, Info, XCircle, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { format, isBefore, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { useState, useMemo, useRef, useCallback } from "react";

export const Route = createFileRoute("/admin/attendance/")({
	component: AttendanceManagementPage,
});

type HourCellState = "present" | "absent" | "empty";

function AttendanceManagementPage() {
	const queryClient = useQueryClient();
	const [selectedSchool, setSelectedSchool] = useState<string>("");
	const [selectedDate, setSelectedDate] = useState<string>(
		format(new Date(), "yyyy-MM-dd")
	)
	const [courseFilter, setCourseFilter] = useState<string>("");
	const [hourlyAttendanceData, setHourlyAttendanceData] = useState<StudentAttendanceRow[]>([]);
	const [hourlyCellStates, setHourlyCellStates] = useState<Record<string, Record<string, HourCellState>>>({});
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [importResult, setImportResult] = useState<ExcelImportResult | null>(null);
	const [showImportDialog, setShowImportDialog] = useState(false);
	const [pendingNewEnrollments, setPendingNewEnrollments] = useState<NewEnrollmentExcelRow[]>([]);
	const [sendAbsenceSms, setSendAbsenceSms] = useState(false);
	const [sortColumn, setSortColumn] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	const handleSort = useCallback((column: string) => {
		if (sortColumn === column) {
			if (sortDirection === "asc") {
				setSortDirection("desc");
			} else {
				setSortColumn(null);
				setSortDirection("asc");
			}
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	}, [sortColumn, sortDirection]);

	const { data: schoolsData } = useQuery(getSchoolsOptions());
	const schools =
		(schoolsData as any as Array<{ value: string; label: string }>) || [];

	const { data: coursesData } = useQuery(getAllCoursesOptions());
	const allCourses = (coursesData as any as Course[]) || [];

	const { data: schoolDayData, isLoading } = useQuery<SchoolDayAttendance>({
		queryKey: ["attendance", "school-day", selectedSchool, selectedDate],
		queryFn: async () => {
			const params = new URLSearchParams({
				school: selectedSchool,
				date: selectedDate,
			})
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/attendances/by-school-and-date?${params}`,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			)
			if (!response.ok) throw new Error("Erreur lors du chargement des données");
			return response.json();
		},
		enabled: !!selectedSchool && !!selectedDate,
	})

	const saveBulkMutation = useMutation({
		mutationFn: async (requests: any[]) => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/attendances/bulk?sendSms=${sendAbsenceSms}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify(requests),
				}
			)
			if (!response.ok) throw new Error("Erreur lors de l'enregistrement");
			return response.json();
		},
		onSuccess: () => {
			toast.success(
				sendAbsenceSms
					? "Présences enregistrées avec succès. Les SMS d'absence seront envoyés automatiquement."
					: "Présences enregistrées avec succès (sans envoi de SMS)."
			);
			queryClient.invalidateQueries({ queryKey: ["attendance", "school-day", selectedSchool, selectedDate] });
		},
		onError: () => {
			toast.error("Erreur lors de l'enregistrement des présences");
		},
	})

	const coursesForSelectedSchool = useMemo(() => {
		if (!selectedSchool) return [];
		const today = new Date();
		return allCourses.filter((course: any) => {
			if (course.school !== selectedSchool) return false;
			if (!course.startDate) return false;
			const startDate = new Date(course.startDate);
			return isBefore(startDate, today) || isSameDay(startDate, today);
		})
	}, [allCourses, selectedSchool]);

	const filteredAttendanceData = useMemo(() => {
		if (!courseFilter) return hourlyAttendanceData;
		return hourlyAttendanceData.filter(row => row.courseId === courseFilter);
	}, [hourlyAttendanceData, courseFilter]);

	const sortedAttendanceData = useMemo(() => {
		if (!sortColumn) return filteredAttendanceData;

		const sorted = [...filteredAttendanceData].sort((a, b) => {
			let valA: string;
			let valB: string;

			switch (sortColumn) {
				case "class":
					valA = a.className || "";
					valB = b.className || "";
					break;
				case "lastName":
					valA = a.studentLastName;
					valB = b.studentLastName;
					break;
				case "firstName":
					valA = a.studentFirstName;
					valB = b.studentFirstName;
					break;
				case "subject":
					valA = a.courseSubject || "";
					valB = b.courseSubject || "";
					break;
				case "schedule":
					valA = a.startTime;
					valB = b.startTime;
					break;
				case "teacher":
					valA = a.teacherName || "";
					valB = b.teacherName || "";
					break;
				case "school":
					valA = a.school;
					valB = b.school;
					break;
				case "parentPhone":
					valA = a.parentPhone || "";
					valB = b.parentPhone || "";
					break;
				default:
					return 0;
			}

			return valA.localeCompare(valB, "fr", { sensitivity: "base" });
		});

		return sortDirection === "desc" ? sorted.reverse() : sorted;
	}, [filteredAttendanceData, sortColumn, sortDirection]);

	const handleGenerateExcel = async () => {
		if (!schoolDayData || !selectedSchool || !selectedDate) {
			toast.error("Aucune donnée à exporter");
			return
		}
		try {
			const params = new URLSearchParams({
				school: selectedSchool,
				date: selectedDate,
			});
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/attendances/export-excel?${params}`,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);

			if (!response.ok) throw new Error("Erreur lors de la génération");

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `presences_${selectedSchool}_${selectedDate}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success("Fichier Excel téléchargé avec succès");
		} catch (error) {
			toast.error("Erreur lors de la génération du fichier Excel");
			console.error(error);
		}
	}

	const handleImportExcel = () => {
		fileInputRef.current?.click();
	}

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !selectedSchool || !selectedDate) return;

		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/attendances/import-excel?school=${selectedSchool}&date=${selectedDate}`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: formData,
				}
			);

			if (!response.ok) throw new Error("Erreur lors de l'import");

			const result: ExcelImportResult = await response.json();

			// Store import result and show dialog
			setImportResult(result);
			setShowImportDialog(true);

			// Apply attendance requests to the UI
			const updatedData = result.attendanceRequests.map((req: any) => {
				const original = schoolDayData?.attendances.find(
					a => a.studentId === req.studentId && a.courseId === req.courseId
				);
				return {
					...original,
					hourlyAttendance: req.hourlyAttendance,
					overtimeHours: req.overtimeHours || 0,
					notes: req.notes,
					status: req.status,
				};
			});

			setHourlyAttendanceData(updatedData as StudentAttendanceRow[]);

			const newCellStates: Record<string, Record<string, HourCellState>> = {};
			for (const req of result.attendanceRequests) {
				const rowKey = `${req.studentId}_${req.courseId}`;
				newCellStates[rowKey] = {};
				for (const [slot, value] of Object.entries(req.hourlyAttendance as Record<string, boolean>)) {
					if (value) {
						newCellStates[rowKey][slot] = "present";
					} else if (req.status === "ABSENT") {
						newCellStates[rowKey][slot] = "absent";
					} else {
						newCellStates[rowKey][slot] = "empty";
					}
				}
			}
			setHourlyCellStates(newCellStates);

			// Store new enrollments for processing on save
			setPendingNewEnrollments(result.newEnrollments || []);

			if (result.totalErrors > 0) {
				toast.error(`Import avec ${result.totalErrors} erreur(s). Consultez le rapport.`);
			} else {
				toast.success("Fichier Excel importé avec succès");
			}
		} catch (error) {
			toast.error("Erreur lors de l'import du fichier Excel");
			console.error(error);
		}

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}

	const isHourInCourseRange = (row: StudentAttendanceRow, hourSlot: string) => {
		const [startStr] = hourSlot.split("-");
		const [startHour, startMin] = startStr.replace("h", ":").split(":");
		const slotStart = `${startHour.padStart(2, "0")}:${startMin.padStart(2, "0")}`;

		const courseStart = row.startTime.substring(0, 5);
		const courseEnd = row.endTime.substring(0, 5);

		return slotStart >= courseStart && slotStart < courseEnd;
	}

	const getCellState = (row: StudentAttendanceRow, slot: string): HourCellState => {
		const rowKey = `${row.studentId}_${row.courseId}`;
		const state = hourlyCellStates[rowKey]?.[slot];
		if (state) return state;

		if (row.hourlyAttendance[slot]) return "present";
		if (row.status === "ABSENT" && row.attendanceId) return "absent";
		return "empty";
	}

	const setCellState = (row: StudentAttendanceRow, slot: string, state: HourCellState) => {
		const rowKey = `${row.studentId}_${row.courseId}`;
		setHourlyCellStates(prev => ({
			...prev,
			[rowKey]: {
				...(prev[rowKey] || {}),
				[slot]: state,
			}
		}));

		const originalIndex = hourlyAttendanceData.findIndex(
			r => r.studentId === row.studentId && r.courseId === row.courseId
		);
		if (originalIndex === -1) return;

		const newData = [...hourlyAttendanceData];
		newData[originalIndex] = {
			...newData[originalIndex],
			hourlyAttendance: {
				...newData[originalIndex].hourlyAttendance,
				[slot]: state === "present",
			},
		};
		setHourlyAttendanceData(newData);
	}

	const handleCellClick = (row: StudentAttendanceRow, slot: string) => {
		const current = getCellState(row, slot);
		let next: HourCellState;
		if (current === "empty") {
			next = "present";
		} else if (current === "present") {
			next = "empty";
		} else {
			next = "empty";
		}
		setCellState(row, slot, next);
	}

	const handleHourlyNotesChange = (index: number, notes: string) => {
		const newData = [...hourlyAttendanceData];
		newData[index] = {
			...newData[index],
			notes,
		}
		setHourlyAttendanceData(newData);
	}

	const handleOvertimeChange = (index: number, value: string) => {
		const newData = [...hourlyAttendanceData];
		const parsed = parseInt(value, 10);
		newData[index] = {
			...newData[index],
			overtimeHours: isNaN(parsed) || parsed < 0 ? 0 : parsed,
		}
		setHourlyAttendanceData(newData);
	}

	const handleSaveBulk = async () => {
		const requests = hourlyAttendanceData.map((row) => {
			const rowKey = `${row.studentId}_${row.courseId}`;
			const cellStates = hourlyCellStates[rowKey] || {};

			const hourlyAttendance: Record<string, boolean> = {};
			let hasAbsent = false;

			for (const [slot, value] of Object.entries(row.hourlyAttendance)) {
				const state = cellStates[slot];
				if (state === "present") {
					hourlyAttendance[slot] = true;
				} else if (state === "absent") {
					hourlyAttendance[slot] = false;
					hasAbsent = true;
				} else if (state === undefined) {
					hourlyAttendance[slot] = value;
					if (!value && row.status === "ABSENT") {
						hasAbsent = true;
					}
				} else {
					hourlyAttendance[slot] = false;
				}
			}

			const hoursPresent = Object.values(hourlyAttendance).filter(Boolean).length;
			let status: string;
			if (hoursPresent > 0) {
				status = "PRESENT";
			} else if (hasAbsent) {
				status = "ABSENT";
			} else {
				status = "ABSENT";
			}

			return {
				studentId: row.studentId,
				courseId: row.courseId,
				date: row.date,
				status,
				hourlyAttendance,
				overtimeHours: row.overtimeHours || 0,
				notes: row.notes,
			}
		})

		await saveBulkMutation.mutateAsync(requests);

		// Process new enrollments if any
		if (pendingNewEnrollments.length > 0) {
			try {
				const enrollResponse = await fetch(
					`${import.meta.env.VITE_API_URL}/api/admin/attendances/process-new-enrollments?date=${selectedDate}`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
						body: JSON.stringify(pendingNewEnrollments),
					}
				);

				if (!enrollResponse.ok) throw new Error("Erreur lors du traitement des nouvelles inscriptions");

				const processedEnrollments: NewEnrollmentExcelRow[] = await enrollResponse.json();
				const sentCount = processedEnrollments.filter(e => e.smsSent).length;

				if (sentCount > 0) {
					toast.success(`${sentCount} lien(s) d'inscription envoyé(s) par SMS`);
				}
				if (sentCount < processedEnrollments.length) {
					toast.error(`${processedEnrollments.length - sentCount} lien(s) d'inscription en erreur`);
				}

				setPendingNewEnrollments([]);
			} catch (error) {
				toast.error("Erreur lors du traitement des nouvelles inscriptions");
				console.error(error);
			}
		}
	}

	const markRowAbsent = (row: StudentAttendanceRow) => {
		const rowKey = `${row.studentId}_${row.courseId}`;
		const newStates: Record<string, HourCellState> = {};

		if (schoolDayData) {
			for (const slot of schoolDayData.hourSlots) {
				if (isHourInCourseRange(row, slot)) {
					newStates[slot] = "absent";
				}
			}
		}

		setHourlyCellStates(prev => ({
			...prev,
			[rowKey]: {
				...(prev[rowKey] || {}),
				...newStates,
			}
		}));

		const originalIndex = hourlyAttendanceData.findIndex(
			r => r.studentId === row.studentId && r.courseId === row.courseId
		);
		if (originalIndex === -1) return;

		const newHourly = { ...hourlyAttendanceData[originalIndex].hourlyAttendance };
		if (schoolDayData) {
			for (const slot of schoolDayData.hourSlots) {
				if (isHourInCourseRange(row, slot)) {
					newHourly[slot] = false;
				}
			}
		}

		const newData = [...hourlyAttendanceData];
		newData[originalIndex] = {
			...newData[originalIndex],
			hourlyAttendance: newHourly,
		};
		setHourlyAttendanceData(newData);
	}

	const markRowPresent = (row: StudentAttendanceRow) => {
		const rowKey = `${row.studentId}_${row.courseId}`;
		const newStates: Record<string, HourCellState> = {};

		if (schoolDayData) {
			for (const slot of schoolDayData.hourSlots) {
				if (isHourInCourseRange(row, slot)) {
					newStates[slot] = "present";
				}
			}
		}

		setHourlyCellStates(prev => ({
			...prev,
			[rowKey]: {
				...(prev[rowKey] || {}),
				...newStates,
			}
		}));

		const originalIndex = hourlyAttendanceData.findIndex(
			r => r.studentId === row.studentId && r.courseId === row.courseId
		);
		if (originalIndex === -1) return;

		const newHourly = { ...hourlyAttendanceData[originalIndex].hourlyAttendance };
		if (schoolDayData) {
			for (const slot of schoolDayData.hourSlots) {
				if (isHourInCourseRange(row, slot)) {
					newHourly[slot] = true;
				}
			}
		}

		const newData = [...hourlyAttendanceData];
		newData[originalIndex] = {
			...newData[originalIndex],
			hourlyAttendance: newHourly,
		};
		setHourlyAttendanceData(newData);
	}

	useMemo(() => {
		if (schoolDayData?.attendances) {
			setHourlyAttendanceData(schoolDayData.attendances);

			const newCellStates: Record<string, Record<string, HourCellState>> = {};
			for (const att of schoolDayData.attendances) {
				const rowKey = `${att.studentId}_${att.courseId}`;
				newCellStates[rowKey] = {};
				for (const [slot, value] of Object.entries(att.hourlyAttendance)) {
					if (value) {
						newCellStates[rowKey][slot] = "present";
					} else if (att.status === "ABSENT" && att.attendanceId) {
						newCellStates[rowKey][slot] = "absent";
					} else {
						newCellStates[rowKey][slot] = "empty";
					}
				}
			}
			setHourlyCellStates(newCellStates);
		}
	}, [schoolDayData]);

	const renderCellContent = (row: StudentAttendanceRow, slot: string) => {
		const state = getCellState(row, slot);
		if (state === "present") {
			return (
				<div className="w-6 h-6 rounded bg-green-500 text-white flex items-center justify-center text-xs font-bold cursor-pointer"
					onClick={() => handleCellClick(row, slot)}
					title="Présent - Cliquer pour marquer absent">
					X
				</div>
			);
		}
		if (state === "absent") {
			return (
				<div className="w-10 h-6 rounded bg-red-500 text-white flex items-center justify-center text-xs font-bold cursor-pointer"
					onClick={() => handleCellClick(row, slot)}
					title="Absent - Cliquer pour effacer">
					ABS
				</div>
			);
		}
		return (
			<div className="w-6 h-6 rounded border border-gray-300 cursor-pointer hover:bg-gray-100"
				onClick={() => handleCellClick(row, slot)}
				title="Non renseigné - Cliquer pour marquer présent" />
		);
	}

	const SortableHeader = ({ column, title }: { column: string; title: string }) => {
		const isActive = sortColumn === column;
		return (
			<th className="p-2 text-left">
				<button
					type="button"
					className="inline-flex items-center gap-1 hover:text-black text-gray-600 font-semibold text-sm"
					onClick={() => handleSort(column)}
				>
					{title}
					{isActive && sortDirection === "asc" ? (
						<ArrowUp className="h-3.5 w-3.5" />
					) : isActive && sortDirection === "desc" ? (
						<ArrowDown className="h-3.5 w-3.5" />
					) : (
						<ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
					)}
				</button>
			</th>
		);
	};

	const getIssueIcon = (type: ExcelImportIssue["type"]) => {
		switch (type) {
			case "ERROR":
				return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
			case "WARNING":
				return <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0" />;
			case "INFO":
				return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
		}
	}

	const getIssueBadgeVariant = (type: ExcelImportIssue["type"]) => {
		switch (type) {
			case "ERROR":
				return "destructive" as const;
			case "WARNING":
				return "outline" as const;
			case "INFO":
				return "secondary" as const;
		}
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold">Gestion des présences</h1>
				<p className="text-gray-600 mt-2">
					Enregistrez les présences des élèves par heure
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Sélection</CardTitle>
					<CardDescription>
						Choisissez l'établissement et la date
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-3 gap-4">
						<div>
							<Label>Établissement</Label>
							<Select
								value={selectedSchool}
								onValueChange={(value) => {
									setSelectedSchool(value)
									setCourseFilter("")
									setHourlyAttendanceData([]);
									setHourlyCellStates({});
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="Sélectionnez un établissement" />
								</SelectTrigger>
								<SelectContent>
									{schools.map((school) => (
										<SelectItem key={school.value} value={school.value}>
											{school.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div>
							<Label>Date</Label>
							<Input
								type="date"
								value={selectedDate}
								onChange={(e) => {
									setSelectedDate(e.target.value);
									setCourseFilter("")
									setHourlyAttendanceData([]);
									setHourlyCellStates({});
								}}
							/>
						</div>

						<div>
							<Label>Filtre par cours (optionnel)</Label>
							<Select
								value={courseFilter || "all"}
								onValueChange={(value) => setCourseFilter(value === "all" ? "" : value)}
							>
								<SelectTrigger>
									<SelectValue placeholder="Tous les cours" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Tous les cours</SelectItem>
									{coursesForSelectedSchool.map((course: any) => (
										<SelectItem key={course.id} value={course.id}>
											{course.subject} - {course.level}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{selectedSchool && selectedDate && schoolDayData && (
				<Card>
					<CardHeader>
						<div className="flex justify-between items-start">
							<div>
								<CardTitle>
									Présences du {format(new Date(selectedDate), "dd MMMM yyyy", { locale: fr })}
								</CardTitle>
								<CardDescription>
									{sortedAttendanceData.length} élève(s) - {schoolDayData.hourSlots.length} créneau(x) horaire(s)
								</CardDescription>
								<div className="flex gap-4 mt-2 text-xs">
									<div className="flex items-center gap-1">
										<div className="w-4 h-4 rounded bg-green-500" />
										<span>Présent (X)</span>
									</div>
									<div className="flex items-center gap-1">
										<div className="w-4 h-4 rounded bg-red-500" />
										<span>Absent (ABS)</span>
									</div>
									<div className="flex items-center gap-1">
										<div className="w-4 h-4 rounded border border-gray-300" />
										<span>Non renseigné</span>
									</div>
								</div>
							</div>
							<div className="flex gap-2">
								<Button onClick={handleGenerateExcel} variant="outline" size="sm">
									<Download className="mr-2 h-4 w-4" />
									Générer Excel
								</Button>
								<Button variant="outline" onClick={handleImportExcel} size="sm">
									<Upload className="mr-2 h-4 w-4" />
									Importer Excel
								</Button>
								<input
									ref={fileInputRef}
									type="file"
									accept=".xlsx,.xls"
									className="hidden"
									onChange={handleFileChange}
								/>
							</div>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						{sortedAttendanceData.length > 0 ? (
							<>
								<div className="overflow-x-auto">
									<table className="w-full border-collapse text-sm">
										<thead>
											<tr className="border-b bg-gray-50">
												<th className="p-2 text-left sticky left-0 bg-gray-50 z-10">
													<button
														type="button"
														className="inline-flex items-center gap-1 hover:text-black text-gray-600 font-semibold text-sm"
														onClick={() => handleSort("class")}
													>
														Classe
														{sortColumn === "class" && sortDirection === "asc" ? (
															<ArrowUp className="h-3.5 w-3.5" />
														) : sortColumn === "class" && sortDirection === "desc" ? (
															<ArrowDown className="h-3.5 w-3.5" />
														) : (
															<ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
														)}
													</button>
												</th>
												<SortableHeader column="lastName" title="Nom" />
												<SortableHeader column="firstName" title="Prénom" />
												<SortableHeader column="subject" title="Matière" />
												<SortableHeader column="schedule" title="Horaire" />
												<SortableHeader column="teacher" title="Prof" />
												<SortableHeader column="school" title="Établissement" />
												<SortableHeader column="parentPhone" title="Tél. parent" />
												{schoolDayData.hourSlots.map((slot) => (
													<th key={slot} className="p-2 text-center text-xs">
														{slot}
													</th>
												))}
												<th className="p-2 text-center text-xs">Heures sup.</th>
												<th className="p-2 text-center text-xs">Actions</th>
												<th className="p-2 text-left">Notes</th>
											</tr>
										</thead>
										<tbody>
											{sortedAttendanceData.map((row) => {
												const originalIndex = hourlyAttendanceData.findIndex(
													r => r.studentId === row.studentId && r.courseId === row.courseId
												)

												return (
													<tr key={`${row.studentId}-${row.courseId}`} className="border-b">
														<td className="p-2 sticky left-0 bg-white z-10">
															{row.className || "-"}
														</td>
														<td className="p-2 font-medium">
															{row.studentLastName}
														</td>
														<td className="p-2">
															{row.studentFirstName}
														</td>
														<td className="p-2 text-xs">
															{row.courseSubject || "-"}
														</td>
														<td className="p-2 text-xs">
															{row.startTime} - {row.endTime}
														</td>
														<td className="p-2 text-xs">
															{row.teacherName || "-"}
														</td>
														<td className="p-2 text-xs">
															{row.school}
														</td>
														<td className="p-2 text-xs">
															{row.parentPhone || "-"}
														</td>
														{schoolDayData.hourSlots.map((slot) => {
															const isInRange = isHourInCourseRange(row, slot);

															return (
																<td key={slot} className={`p-2 text-center ${!isInRange ? "bg-gray-100" : ""}`}>
																	{isInRange && renderCellContent(row, slot)}
																</td>
															)
														})}
														<td className="p-2 text-center">
															<Input
																type="number"
																min="0"
																value={row.overtimeHours || 0}
																onChange={(e) =>
																	handleOvertimeChange(originalIndex, e.target.value)
																}
																className="w-16 text-center text-sm"
															/>
														</td>
														<td className="p-2 text-center">
															<div className="flex gap-1 justify-center">
																<Button
																	variant="outline"
																	size="sm"
																	className="h-6 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50"
																	onClick={() => markRowPresent(row)}
																	title="Marquer présent"
																>
																	X
																</Button>
																<Button
																	variant="outline"
																	size="sm"
																	className="h-6 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50"
																	onClick={() => markRowAbsent(row)}
																	title="Marquer absent"
																>
																	ABS
																</Button>
															</div>
														</td>
														<td className="p-2">
															<Input
																value={row.notes || ""}
																onChange={(e) =>
																	handleHourlyNotesChange(originalIndex, e.target.value)
																}
																placeholder="Notes..."
																className="text-sm"
															/>
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>

								{pendingNewEnrollments.length > 0 && (
									<div className="rounded-lg border border-blue-200 bg-blue-50 p-3 mt-4">
										<h4 className="font-medium text-blue-800 text-sm mb-1">
											{pendingNewEnrollments.length} nouvelle(s) inscription(s) en attente
										</h4>
										<p className="text-xs text-blue-600">
											Les liens d'inscription seront créés et envoyés par SMS au clic sur "Enregistrer".
										</p>
										<div className="mt-2 space-y-1">
											{pendingNewEnrollments.map((e, idx) => (
												<div key={idx} className="text-xs text-blue-700">
													{e.studentFirstName} {e.studentLastName} - {e.parentPhone}
													{e.className && ` (${e.className})`}
												</div>
											))}
										</div>
									</div>
								)}

								<div className="flex justify-end items-center gap-4 mt-4">
									<div className="flex items-center gap-2">
										<Checkbox
											id="sendAbsenceSms"
											checked={sendAbsenceSms}
											onCheckedChange={(checked) => setSendAbsenceSms(checked === true)}
										/>
										<Label htmlFor="sendAbsenceSms" className="text-sm cursor-pointer">
											Envoyer les SMS d'absence
										</Label>
									</div>
									<Button onClick={handleSaveBulk} disabled={saveBulkMutation.isPending}>
										<Save className="mr-2 h-4 w-4" />
										{saveBulkMutation.isPending ? "Enregistrement..." : "Enregistrer"}
										{pendingNewEnrollments.length > 0 && (
											<Badge variant="secondary" className="ml-2">
												+{pendingNewEnrollments.length} inscription{pendingNewEnrollments.length > 1 ? "s" : ""}
											</Badge>
										)}
									</Button>
								</div>
							</>
						) : (
							<div className="py-12 text-center text-gray-500">
								<Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
								<p className="font-medium">Aucun élève</p>
								<p className="text-sm mt-1">
									Aucun élève inscrit pour cette date et ce filtre
								</p>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{isLoading && selectedSchool && selectedDate && (
				<Card>
					<CardContent className="py-12 text-center">
						Chargement des données...
					</CardContent>
				</Card>
			)}

			{/* Import Results Dialog */}
			<Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							{importResult && importResult.totalErrors > 0 ? (
								<AlertTriangle className="h-5 w-5 text-yellow-500" />
							) : (
								<CheckCircle className="h-5 w-5 text-green-500" />
							)}
							Rapport d'import Excel
						</DialogTitle>
						<DialogDescription>
							Résumé de l'import du fichier de présences
						</DialogDescription>
					</DialogHeader>

					{importResult && (
						<div className="space-y-4">
							{/* Summary */}
							<div className="grid grid-cols-2 gap-3">
								<div className="rounded-lg border p-3">
									<div className="text-sm text-gray-500">Élèves présents</div>
									<div className="text-2xl font-bold text-green-600">
										{importResult.totalStudentsPresent}
									</div>
								</div>
								<div className="rounded-lg border p-3">
									<div className="text-sm text-gray-500">Élèves absents</div>
									<div className="text-2xl font-bold text-red-600">
										{importResult.totalStudentsAbsent}
									</div>
								</div>
								<div className="rounded-lg border p-3">
									<div className="text-sm text-gray-500">Nouvelles inscriptions</div>
									<div className="text-2xl font-bold text-blue-600">
										{importResult.totalNewEnrollments}
									</div>
								</div>
								<div className="rounded-lg border p-3">
									<div className="text-sm text-gray-500">Erreurs</div>
									<div className={`text-2xl font-bold ${importResult.totalErrors > 0 ? "text-red-600" : "text-gray-400"}`}>
										{importResult.totalErrors}
									</div>
								</div>
							</div>

							{/* New enrollments info */}
							{importResult.newEnrollments.length > 0 && (
								<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
									<h4 className="font-medium text-blue-800 mb-2">
										Nouvelles inscriptions ({importResult.newEnrollments.length})
									</h4>
									<div className="space-y-1">
										{importResult.newEnrollments.map((enrollment, idx) => (
											<div key={idx} className="flex items-center justify-between text-sm">
												<span className="text-blue-700">
													{enrollment.studentFirstName} {enrollment.studentLastName}
													{enrollment.className && ` (${enrollment.className})`}
												</span>
												<div className="flex items-center gap-2">
													<span className="text-blue-600 text-xs">
														{enrollment.parentPhone}
													</span>
													{enrollment.smsSent ? (
														<Badge variant="secondary" className="text-xs">SMS envoyé</Badge>
													) : (
														<Badge variant="outline" className="text-xs text-red-600">SMS non envoyé</Badge>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Issues list */}
							{importResult.issues.length > 0 && (
								<div className="space-y-2">
									<h4 className="font-medium">
										Détails ({importResult.issues.length} message{importResult.issues.length > 1 ? "s" : ""})
									</h4>
									<div className="space-y-1 max-h-60 overflow-y-auto">
										{importResult.issues.map((issue, idx) => (
											<div key={idx} className="flex items-start gap-2 rounded border p-2 text-sm">
												{getIssueIcon(issue.type)}
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<Badge variant={getIssueBadgeVariant(issue.type)} className="text-xs">
															{issue.type}
														</Badge>
														{issue.studentName && (
															<span className="font-medium">{issue.studentName}</span>
														)}
														{issue.rowNumber > 0 && (
															<span className="text-gray-400 text-xs">Ligne {issue.rowNumber}</span>
														)}
													</div>
													<p className="text-gray-600 mt-0.5">{issue.message}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{importResult.issues.length === 0 && (
								<div className="text-center py-4 text-gray-500">
									<CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
									<p>Aucun problème détecté</p>
								</div>
							)}

							<div className="flex justify-end">
								<Button onClick={() => setShowImportDialog(false)}>
									Fermer
								</Button>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	)
}
